import React, {FC, ReactElement, useCallback, useEffect, useMemo, useState} from "react";
import {Autocomplete, Grid, TextField} from "@mui/material";
import LoaderWithCompanySelected from "../../components/LoaderWithCompanySelected";
import DriveFile from "../../model/file/DriveFile";
import MyMaterialTable from "../../components/mtable/MyMaterialTable";
import PreviewIcon from '@mui/icons-material/Preview';
import Icon from "@mui/material/Icon";
import LoadingButton from "../../components/button/LoadingButton";
import {emailTemplateApi} from "../../backend/EmailTemplateApi";
import {HtmlTextEditor} from "./HtmlTextEditor";
import {createFilterOptions} from "@mui/material/useAutocomplete";
import * as Yup from "yup";
import {Trans, Translation} from "react-i18next";
import {observer} from "mobx-react";
import {accountEmailStateStore} from "../../store/AccountEmailState";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import ButtonWithConfirmation from "../../components/button/ButtonWithConfirmation";
import {logEvent} from "../../utils/SentryUtils";
import {notificationStore} from "../../store/NotificationStore";
import ClearIcon from '@mui/icons-material/Clear';
import {DropzoneAreaBase, FileObject} from "react-mui-dropzone";
import makeStyles from "@mui/styles/makeStyles";
import {getFileBase64} from "../../utils/ReadFile";

interface EmailOption {
    title: string
    email: string
}

const filter = createFilterOptions<EmailOption>();

const useStyles = makeStyles({
    dropZone: {
        width: "100%",
        "& .MuiDropzoneArea-root": {
            height: "9rem",
            minHeight: "unset"
        }
    },
});

interface IProps {

}

export const EmailCommunicationView: FC<IProps> = observer((): ReactElement => {
    const classes = useStyles();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [files, setFiles] = useState<DriveFile[]>([]);

    useEffect(() => {
        setLoading(true);
        emailTemplateApi.getEmailTemplates()
            .then(data => {
                setFiles(data)
                setLoading(false)
                logEvent("accountant-communication", "Loaded templates")
            })
            .catch(setError)
    }, []);

    const sendToSelectValues: EmailOption[] = useMemo(() => (accountEmailStateStore.getTo() ?? []).map(email => ({title: email, email})), [accountEmailStateStore.getTo()]);
    const subject = accountEmailStateStore.getSubject() ?? "";
    const body = accountEmailStateStore.getBody();

    const [filesToAttach, setFilesToAttach] = useState<File[]>([]);
    const doAddAttachment = useCallback((toAdd: FileObject[]) => {
        setFilesToAttach(prev => [...prev, ...toAdd.map(it => it.file)])
    }, [setFilesToAttach]);

    const onDeleteAttachment = useCallback((deletedFileObject: FileObject, index: number) => {
        setFilesToAttach(prev => prev.filter(it => it !== deletedFileObject.file))
    }, [setFilesToAttach]);

    const fileObjects: FileObject[] = useMemo(() => {
        return filesToAttach.map(it => ({file: it, data: null}))
    }, [filesToAttach]);

    const loadDefaults = useCallback(async () => {
        logEvent("accountant-communication", `Going to fetch defaults`)
        const defaults = await emailTemplateApi.openDefaults()
        accountEmailStateStore.setTemplate(defaults)
        logEvent("accountant-communication", `Defaults loaded`)
        setFilesToAttach([])
    }, []);

    const maxAttachmentSize = 4500000; //4.5MB
    const totalAttachmentSize = useMemo(() => filesToAttach.map(it => it.size).reduce((a, b) => a + b, 0), [filesToAttach])

    const sendDisabled = !((accountEmailStateStore.getTo() ?? []).length)
        || !accountEmailStateStore.getSubject()?.length
        || !accountEmailStateStore.getBody()?.length
        || totalAttachmentSize > maxAttachmentSize;

    return <>
        <LoaderWithCompanySelected loading={false} error={error}>
            <Grid container>
                <Grid item container xs={12} columnSpacing={2}>
                    <Grid item xs={6}>
                        <MyMaterialTable
                            title="Email templates"
                            columns={[
                                {
                                    field: "name",
                                    render: (row: DriveFile) =>
                                        <Grid container spacing={1} alignItems="center" justifyContent="flex-start">
                                            <Grid item>
                                                <Icon>
                                                    <img alt={row.name} src={row.iconLink} height={16} width={16}/>
                                                </Icon>
                                            </Grid>
                                            <Grid item>{row.name}</Grid>
                                        </Grid>
                                },
                            ]}
                            isLoading={loading}
                            data={files}
                            options={{
                                search: true,
                                header: false,
                                sorting: true,
                                paging: false,
                                draggable: false,
                                actionsColumnIndex: 1000,
                            }}
                            actions={[
                                (file: DriveFile) => ({
                                    icon: () =>
                                        <LoadingButton variant="contained" color="primary" startIcon={<PreviewIcon/>}
                                                       onClick={async () => {
                                                           logEvent("accountant-communication", `Going to open template ${file.id}`)
                                                           const template = await emailTemplateApi.openEmailTemplate(file.id);
                                                           logEvent("accountant-communication", `Template ${file.id} loaded`)
                                                           accountEmailStateStore.setTemplate(template);
                                                       }}>
                                            Open
                                        </LoadingButton>,
                                    onClick: () => {
                                    }
                                }),
                            ]}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <div>
                            <Grid item container xs={12} rowSpacing={2}>
                                <Grid item xs={12}>
                                    <Autocomplete
                                        multiple
                                        options={[]}
                                        value={sendToSelectValues}
                                        getOptionLabel={(option: EmailOption) => option.title}
                                        filterOptions={(options, params) => {
                                            const filtered = filter(options, params);

                                            if (params.inputValue !== '' && Yup.string().email().isValidSync(params.inputValue)) {
                                                filtered.push({
                                                    email: params.inputValue,
                                                    title: `Add "${params.inputValue}"`,
                                                });
                                            }

                                            return filtered;
                                        }}
                                        noOptionsText={"Type valid email to add"}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="standard"
                                                label="Send to"
                                            />
                                        )}
                                        onChange={(event, newValue) => {
                                            accountEmailStateStore.setTo(newValue.map(it => it.email));
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Subject"
                                        value={subject}
                                        fullWidth={true}
                                        onChange={e => accountEmailStateStore.setSubject(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <HtmlTextEditor value={body} onChange={value => accountEmailStateStore.setBody(value)}/>
                                </Grid>
                                <Grid item container xs={12}>
                                    <div className={classes.dropZone}>
                                        <Translation>
                                            {t => {
                                                return <DropzoneAreaBase
                                                    maxFileSize={maxAttachmentSize}
                                                    dropzoneText={t("common.dropzone", "Drag and drop a file here or click")}
                                                    filesLimit={10}
                                                    showPreviews={false}
                                                    showPreviewsInDropzone={true}
                                                    showAlerts={true}
                                                    useChipsForPreview={true}
                                                    showFileNamesInPreview={true}
                                                    fileObjects={fileObjects}
                                                    onAdd={doAddAttachment}
                                                    onDelete={onDeleteAttachment}
                                                />;
                                            }
                                            }
                                        </Translation>
                                    </div>
                                </Grid>
                                <Grid item container xs={12} columnSpacing={1} justifyContent={"flex-end"}>
                                    <Grid item>
                                        <ButtonWithConfirmation
                                            disabled={sendDisabled}
                                            startIcon={<FlightTakeoffIcon/>}
                                            color="success"
                                            variant="contained"
                                            onClick={async () => {
                                                const toAttach = await Promise.all(filesToAttach.map(async it => ({name: it.name, file: await getFileBase64(it)})))
                                                await emailTemplateApi.sendEmailToClient({...accountEmailStateStore.template()!!, files: toAttach})
                                                notificationStore.addNotification("Email successfully sent", {variant: "success"})
                                                logEvent("accountant-communication", `Email sent`)
                                                await loadDefaults();
                                            }}>Send</ButtonWithConfirmation>
                                    </Grid>
                                    <Grid item>
                                        <LoadingButton startIcon={<ClearIcon/>} variant="contained" color="secondary" onClick={loadDefaults}>
                                            <Trans i18nKey="common.clear">Clear</Trans>
                                        </LoadingButton>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </div>
                    </Grid>
                </Grid>
            </Grid>
        </LoaderWithCompanySelected>
    </>
});